import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'

export const RIGHT_TOOLTIP_CLASS =
  'pointer-events-none fixed z-[200] select-none whitespace-nowrap rounded-md bg-slate-600 px-2 py-1 text-xs font-medium text-white shadow-sm dark:bg-slate-950 dark:border-0'

export type TooltipPlacement = 'right' | 'top'

interface RightTooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  /** When false, render children without a tooltip. */
  enabled?: boolean
  /**
   * Classes for the trigger wrapper (default `w-fit`).
   * Use `w-full` in icon rails so children can center inside the column.
   */
  triggerClassName?: string
  /** Default `right` for side rails; use `top` for a collapsed horizontal icon row. */
  placement?: TooltipPlacement
}

const positionForPlacement = (
  rect: DOMRect,
  placement: TooltipPlacement
): { top: number; left: number; transform: string } => {
  if (placement === 'top') {
    // Leave room for the downward arrow between tooltip and trigger.
    return { top: rect.top - 10, left: rect.left + rect.width / 2, transform: 'translate(-50%, -100%)' }
  }
  // Leave room for the left-pointing arrow between trigger and tooltip.
  return { top: rect.top + rect.height / 2, left: rect.right + 10, transform: 'translateY(-50%)' }
}

/** Portaled tooltip for collapsed nav / icon rails. Defaults to the right; `top` for horizontal rails. */
export const RightTooltip: React.FC<RightTooltipProps> = ({
  content,
  children,
  enabled = true,
  triggerClassName,
  placement = 'right',
}) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, transform: 'translateY(-50%)' })

  if (!enabled || content == null || content === '') {
    return <>{children}</>
  }

  const show = (): void => {
    const rect = targetRef.current?.getBoundingClientRect()
    if (rect == null) return
    setPos(positionForPlacement(rect, placement))
    setOpen(true)
  }

  return (
    <>
      <div
        ref={targetRef}
        className={classnames(triggerClassName ?? 'w-fit')}
        onMouseEnter={show}
        onMouseOver={show}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </div>
      {open &&
        createPortal(
          // `fixed` already creates a containing block for the absolute arrow — do not add `relative`
          // (it would override `fixed` in Tailwind and hide the tooltip).
          <div
            role="tooltip"
            className={RIGHT_TOOLTIP_CLASS}
            style={{ top: pos.top, left: pos.left, transform: pos.transform }}
          >
            {placement === 'top' ? (
              <span
                aria-hidden
                className="absolute bottom-0 left-1/2 translate-y-full -translate-x-1/2 border-[5px] border-transparent border-t-slate-600 dark:border-t-slate-950"
              />
            ) : (
              <span
                aria-hidden
                className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 border-[5px] border-transparent border-r-slate-600 dark:border-r-slate-950"
              />
            )}
            {content}
          </div>,
          document.body
        )}
    </>
  )
}

export default RightTooltip
